import { FileDiff, MoreVertical, RotateCcw, Trash2 } from "lucide-react";
import React from "react";
import { formatProjectName } from "../utils";
import { ApprovalStatus } from "./ApprovalStatus";
import { CopyLinkButton } from "./CopyLinkButton";
import { DateTime } from "./DateTime";
import { HighlightedText } from "./HighlightedText";
import {
	MRCommentsIconButton,
	MRCommentsPanel,
	useMRComments,
} from "./MRComments";
import { MRStatusDot } from "./MRStatusDot";
import { DraftIcon, MRStatusIcons } from "./MRStatusIcons";
import { MRTagsEditor } from "./MRTags";
import { OpenInGitLabButton } from "./OpenInGitLabButton";
import { PipelineStatus } from "./PipelineStatus";
import { TruncatedTitleLink } from "./TruncatedTitleLink";

export const SwimlaneCard = ({
	mr,
	onDragStart,
	onDragEnd,
	onDropTop,
	onDropBottom,
	onDragOverTop,
	onDragOverBottom,
	showIndicator,
	isDragging,
	isBeingDragged,
	index,
	onDelete,
	onRestore,
	laneId,
	enableCommentsExpansion = false,
	onOpenDiff,
	searchQuery = "",
	tags = [],
	allTags = [],
	onAddTag,
	onRemoveTag,
}) => {
	const comments = useMRComments(mr);
	const cardRef = React.useRef(null);
	const [showMenu, setShowMenu] = React.useState(false);
	const menuRef = React.useRef(null);
	// Close menu when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setShowMenu(false);
			}
		};

		if (showMenu) {
			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}
	}, [showMenu]);

	if (!mr) return null;

	const isClosed = mr.state === "merged" || mr.state === "closed";
	const projectName = formatProjectName(mr.projectPath);

	const handleDragStart = (e) => {
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", mr.id);

		// Create a custom drag image with opacity
		if (cardRef.current) {
			const clone = cardRef.current.cloneNode(true);
			clone.style.opacity = "0.5";
			clone.style.position = "absolute";
			clone.style.top = "-9999px";
			document.body.appendChild(clone);

			e.dataTransfer.setDragImage(clone, e.offsetX, e.offsetY);

			// Clean up the clone after drag starts
			setTimeout(() => {
				document.body.removeChild(clone);
			}, 0);
		}

		if (onDragStart) {
			onDragStart(mr);
		}
	};

	const handleDragEnd = () => {
		if (onDragEnd) {
			onDragEnd();
		}
	};

	return (
		<div className="relative animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out">
			<div
				ref={cardRef}
				draggable={!!onDragStart}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				className={`interactive-surface rounded-lg p-3 my-2 border border-[var(--color-border)] hover:border-[var(--color-border-hover)] transition-colors relative ${
					onDragStart ? "cursor-grab active:cursor-grabbing" : "cursor-default"
				}`}
			>
				{onDropTop && isDragging && (
					<div
						className="absolute z-10"
						style={{
							top: "-0.5rem",
							left: "-1rem",
							right: "-1rem",
							height: "calc(50% + 0.5rem)",
						}}
						onDragOver={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (onDragOverTop) onDragOverTop();
						}}
						onDrop={onDropTop}
					>
						{showIndicator && (
							<div className="h-1 bg-[var(--color-accent)] rounded-full absolute top-0 left-4 right-4" />
						)}
					</div>
				)}
				{onDropBottom && isDragging && (
					<div
						className="absolute z-10"
						style={{
							bottom: "-0.5rem",
							left: "-1rem",
							right: "-1rem",
							height: "calc(50% + 0.5rem)",
						}}
						onDragOver={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (onDragOverBottom) onDragOverBottom();
						}}
						onDrop={onDropBottom}
					/>
				)}
				{(onDelete || onRestore) && (
					<div className="absolute top-1 right-1 z-20" ref={menuRef}>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setShowMenu(!showMenu);
							}}
							className="p-1 hover:bg-[var(--color-bg-secondary)] rounded-full transition-colors"
							aria-label="More options"
						>
							<MoreVertical className="w-4 h-4 text-[var(--color-text-secondary)]" />
						</button>
						{showMenu && (
							<div className="absolute right-0 mt-1 bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg py-1 z-20 min-w-[120px]">
								{laneId === "deleted" && onRestore ? (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											onRestore(mr.id);
											setShowMenu(false);
										}}
										className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-[var(--color-accent)]"
									>
										<RotateCcw className="w-4 h-4" />
										Restore
									</button>
								) : (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											onDelete(mr.id);
											setShowMenu(false);
										}}
										className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-red-500"
									>
										<Trash2 className="w-4 h-4" />
										Delete
									</button>
								)}
							</div>
						)}
					</div>
				)}
				<div className="flex items-center gap-3">
					<MRStatusDot mr={mr} />
					<div className="flex-1 min-w-0">
						<div className="flex items-start justify-between gap-2 mb-2">
							<TruncatedTitleLink
								href={mr.webUrl}
								title={mr.title}
								query={searchQuery}
								wrapperClassName="flex-1"
								className="line-clamp-1 min-w-0 flex-1 pr-2 text-sm font-semibold text-[var(--color-link)] hover:underline"
								draggable="false"
							/>
							<MRTagsEditor
								mr={mr}
								tags={tags}
								allTags={allTags}
								onAddTag={onAddTag}
								onRemoveTag={onRemoveTag}
								compact={true}
								className={`max-w-[12rem] shrink-0 justify-end ${onDelete || onRestore ? "mr-5" : ""}`}
							/>
						</div>

						<div className="flex items-center justify-between gap-2 mb-2">
							<div className="flex gap-2 items-center flex-wrap">
								<a
									href={mr.webUrl?.split("/-/")[0]}
									target="_blank"
									rel="noopener noreferrer"
									draggable="false"
									className="text-xs text-[var(--color-text-secondary)] hover:underline"
								>
									<HighlightedText query={searchQuery}>
										{projectName}
									</HighlightedText>
								</a>
								<MRStatusIcons mr={mr} iconClass="w-4 h-4" />
							</div>
							<div className="flex flex-shrink-0 items-center gap-3">
								{!isClosed && (
									<>
										<PipelineStatus pipeline={mr.headPipeline} />
										<ApprovalStatus
											aiReviewMissing={mr.aiReviewMissing}
											approvers={mr.approvers}
											reviewers={mr.reviewers}
											approvalsLeft={mr.approvalsLeft}
										/>
									</>
								)}
								<DraftIcon draft={mr.draft} iconClass="w-4 h-4" />
								{enableCommentsExpansion && (
									<MRCommentsIconButton
										comments={comments}
										count={mr.userNotesCount}
									/>
								)}
								{onOpenDiff && (
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											onOpenDiff(mr);
										}}
										className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
										aria-label={`Open diff for !${mr.iid}`}
										title="Open diff"
									>
										<FileDiff className="h-4 w-4" />
									</button>
								)}
								<CopyLinkButton mr={mr} />
								<OpenInGitLabButton mr={mr} />
							</div>
						</div>

						<div className="flex items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-secondary)]">
							{mr.createdAt && (
								<span>
									Created <DateTime date={mr.createdAt} />
								</span>
							)}
							{mr.updatedAt && (
								<span>
									• Updated <DateTime date={mr.updatedAt} />
								</span>
							)}
						</div>
					</div>
				</div>

				{enableCommentsExpansion && (
					<MRCommentsPanel
						comments={comments}
						className="mt-2 pt-2 border-t border-[var(--color-border)]"
					/>
				)}
			</div>
		</div>
	);
};
